import { useCallback, useMemo } from 'react';

import useNavigateWithSearch from '../../../../../hooks/useNavigateWithSearch';
import { BUTTON_SIZE, BUTTON_TYPE, Button } from '../../Button';
import TbbGrey from '../../../images/take-back-bags-gray.svg';
import TbbBoxDefault from '../../../images/tbb_white_front.webp';
import OldTbb from '../../../images/old-tbb.png';
import VirtualBox from '../../../images/Virtual_TechTBB.png';
import { formatMMDDYY } from '../../../utils/dates';
import { shippingProviderStatus, isExpiredLabel } from '../../../utils/easyPost';
import { isBoxRecyclingProductType, isVirtualBoxRecyclingProductType } from '../../../utils/tbbRegistration';
import { REGISTER_CODE_PARAM, ROUTE_TAKE_BACK_BAGS_DETAILS } from '../../../config/routes';
import PrintBatteryLabelButton from '../../../views/Register/Components/PrintBatteryLabelButton';

import style from './TbbItemRow.module.scss';

const {
  container,
  infoContainer,
  button,
  titleStyle,
  subtitleStyle,
  imageStyle,
  dataContainer,
  optionsContainer,
  deliveredDateContainer,
  shippedDateContainer,
  shippedExpiredDateContainer,
  actionContainer,
  imageContainer,
  statusInfo,
} = style;

function getImageSrc(pin, recyclingProductTypeId) {
  if (isVirtualBoxRecyclingProductType(recyclingProductTypeId)) {
    return VirtualBox;
  }

  if (pin) {
    return (
      isBoxRecyclingProductType(recyclingProductTypeId)
    ) ? TbbBoxDefault : TbbGrey;
  }

  return OldTbb;
}

const ItemRow = ({ itemData = {} }) => {
  const navigateWithSearch = useNavigateWithSearch();

  const {
    code: redemptionCode,
    pin,
    redeemedAt,
    recyclingProductTypeId,
    labeldata,
    shipment: {
      shipmentStatus,
      shippingLabelExpiresAt,
      shippedAt,
      deliveredAt,
      shippingMethod,
    } = {},
  } = useMemo(() => itemData, [itemData]);

  const isShipped = useMemo(() => (
    shipmentStatus === shippingProviderStatus.in_transit ||
    shipmentStatus === shippingProviderStatus.out_for_delivery
  ), [shipmentStatus]);

  const isLabelDataEmpty = useMemo(() => {
    if (!labeldata) return true;

    let parsedData = labeldata;
    if (typeof labeldata === 'string') {
      if (!labeldata.trim()) return true;

      try {
        parsedData = JSON.parse(labeldata);
      } catch (e) {
        return true;
      }
    }

    return !parsedData ||
           typeof parsedData !== 'object' ||
           Array.isArray(parsedData) ||
           Object.keys(parsedData).length === 0;
  }, [labeldata]);

  const statusDateTag = useCallback(() => {
    if (shipmentStatus === shippingProviderStatus.delivered) {
      return (
        <div className={deliveredDateContainer}>
          Delivered:
          {' '}
          {formatMMDDYY(deliveredAt)}
        </div>
      );
    }

    if (isShipped) {
      return (
        <div className={shippedDateContainer}>
          Shipped:
          {' '}
          {formatMMDDYY(shippedAt)}
        </div>
      );
    }

    if (isExpiredLabel(shippingLabelExpiresAt)) {
      return (
        <div className={shippedExpiredDateContainer}>
          Label expired:
          {' '}
          {formatMMDDYY(shippingLabelExpiresAt)}
        </div>
      );
    }

    return (
      <div className={shippedDateContainer}>
        Ship by:
        {' '}
        {formatMMDDYY(shippingLabelExpiresAt)}
      </div>
    );
  }, [shipmentStatus, shippingLabelExpiresAt, shippedAt, deliveredAt]);

  return (
    <div className={container}>
      <div className={dataContainer}>
        <div className={imageContainer}>
          <img src={getImageSrc(pin, recyclingProductTypeId)} alt="tbb" className={imageStyle} />
        </div>
        <div className={actionContainer}>
          <div className={infoContainer}>
            <div className={titleStyle}>{`ID: ${itemData.id}`}</div>
            {pin && shipmentStatus !== shippingProviderStatus.delivered && !isShipped && (
              <div className={shippedDateContainer}>{`PIN: ${pin}`}</div>
            )}
            {redeemedAt && shipmentStatus !== shippingProviderStatus.delivered && !isShipped && (
              <div className={subtitleStyle}>{`Registered on: ${formatMMDDYY(redeemedAt)}`}</div>
            )}
            <div className={statusInfo}>
              {shippingMethod?.logoUrl && (
                <img src={shippingMethod?.logoUrl} alt="shipping provider logo" />
              )}
              {statusDateTag()}
            </div>
          </div>
        </div>
      </div>
      {shipmentStatus !== shippingProviderStatus.delivered && !isShipped && !isLabelDataEmpty && (
        <div className={optionsContainer}>
          <Button
            type={BUTTON_TYPE.QUINARY}
            size={BUTTON_SIZE.LARGE}
            className={button}
            onClick={() => {
              navigateWithSearch(
                ROUTE_TAKE_BACK_BAGS_DETAILS,
                { [REGISTER_CODE_PARAM]: redemptionCode },
              );
            }}
          >
            {isExpiredLabel(shippingLabelExpiresAt) ? 'CREATE NEW LABEL' : shippingMethod?.configuration?.myTbbCta}
          </Button>
          {isVirtualBoxRecyclingProductType(recyclingProductTypeId) && (
            <PrintBatteryLabelButton />
          )}
        </div>
      )}
    </div>
  );
};

export default ItemRow;
